import { useEffect, useState } from "react";
import { Menu, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import { ASSETS, NAV_LINKS } from "../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "border-white/10 bg-black/85" : "border-transparent bg-black/40"
      }`}
    >
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-6 lg:px-10">
        <a href="#home" data-testid="nav-logo-link" className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-gold">
          <img src={ASSETS.logo} alt="Legacy Custom Homes" className="h-16 w-16 object-cover" />
          <span className="hidden font-accent text-sm font-semibold uppercase tracking-[0.3em] text-white sm:block">
            Legacy <span className="text-gold">Custom Homes</span>
          </span>
        </a>

        <nav aria-label="Primary" className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-accent text-[11px] font-medium uppercase tracking-[0.25em] text-gray-300 transition-colors duration-300 hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-cta-button"
            className="group flex items-center gap-2 border border-gold/60 px-5 py-2.5 font-accent text-[11px] font-semibold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-black focus:outline-none focus:ring-2 focus:ring-gold"
          >
            Start Your Project
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </a>
        </nav>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="p-2 text-white focus:outline-none focus:ring-2 focus:ring-gold lg:hidden"
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full border-l-white/10 bg-ink sm:max-w-sm">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="mt-14 flex flex-col gap-8">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-serif text-3xl font-medium text-white transition-colors duration-300 hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-button"
                className="mt-4 inline-flex w-fit items-center gap-2 bg-gold px-7 py-3.5 font-accent text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-gold-hover"
              >
                Start Your Project
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
